<template>
  <div>
    <div class="container">
      <Breadcrumbs :breadcrumbs="breadcrumbs"/>

      <h1 v-if="title" v-html="title" class="articles-page__title h2"/>

      <div id="projects-grid" class="articles-page__grid">
        <template v-for="(project) in projects" :key="`project-${ project.id }`">
          <ArticleCard :article="project"/>
        </template>
      </div>

      <div class="articles-page__pagination">
        <Pagination
          :current-page="filter.page"
          :total-pages="pagination.pageCount"
          @change="changePage"
        />
      </div>

    </div>

    <cms-page
      v-if="Boolean(cmsPageIdAfterContent)"
      :page-id="cmsPageIdAfterContent"
      content-only
      hide-head
    />

  </div>
</template>

<script setup lang="ts">
import Pagination from "~/components/UI/Pagination.vue";
import Breadcrumbs from "~/components/UI/Breadcrumbs.vue";
import ArticleCard from "~/components/articles/ArticleCard.vue";

import type { ComputedRef } from 'vue';
import { ArticleItem } from '#sitis/internal/controllers/articles/models/ArticleItem';
import { scrollToElement } from '~/utils/scrollToElement';
import { appConfigStore as useAppConfigStore } from '#sitis/stores/modules/app-store';

const {
  $api
} = useNuxtApp();
const {
  setSEOTitles
} = useSitisCMSPage();
const $route = useRoute();
const $router = useRouter();

const projects = ref([]);
const filter = ref({
  page: Number.parseFloat(String($route?.query?.page || 1))
});
const pagination = ref({ pageCount: 1 });
const getProjects = async () => {
  const res = await $api.agent.get(`/projects?page=${ filter.value.page }&sort=-created_at`).then((res) => {
    return res
  }).catch(() => {
    return null
  });
  if (!res) {
    return null
  }

  const page = filter.value.page || 1;
  let pageUrl = '/3d-projects';
  if (page > 1) {
    pageUrl = [pageUrl, `page=${ page }`].join('?');
  }
  if (process.client) {
    $router.options.history.push(pageUrl);
  }

  const pageCount: number = Number.parseFloat(res.headers.get('x-pagination-page-count') || '1');
  pagination.value = { pageCount: pageCount };

  projects.value = (res._data || []).map((_item: any) => {
    return new ArticleItem(_item)
  });
};
const changePage = async (newPage: number) => {
  scrollToElement('#projects-grid');
  filter.value = { page: newPage };
  await getProjects();
};

const title: ComputedRef<string> = computed(() => {
  return cmsPageInfo.value?.page?.title_tag || ""
});
const breadcrumbs: ComputedRef<any> = computed(() => {
  return [
    { title: "Главная", path: "/" },
    { title: title.value, path: "3d-projects" },
  ]
});

const getCmsPage = async () => {
  return await $api.agent.get(`/cms/page/150`).then((res) => {
    return res?._data || null
  }).catch(() => {
    return null
  });
};
const {
  data: cmsPageInfo
} = useLazyAsyncData('get-cms-page-3d', () => getCmsPage());
useLazyAsyncData('get-articles-list-3d', () => getProjects());

function initSeoData () {
  const _title = cmsPageInfo.value?.page?.title_tag || title.value;
  const _description = cmsPageInfo.value?.page?.description || '';

  setSEOTitles({
    seoTitle: _title,
    seoDescription: _description
  });
};
initSeoData();

const appConfigStore = useAppConfigStore();
const cmsPageIdAfterContent: ComputedRef<number | undefined> = computed(() => {
  const pageId = appConfigStore?.vars?.['3D-проект контент под данными (ID CMS страницы)'] || '';
  if (!pageId) {
    return undefined
  }
  return Number.parseFloat(pageId)
});
</script>

<style scoped lang="scss">
.articles-page__title {
  margin-bottom: 32px;
  max-width: 750px;
}
.articles-page__grid {
  display: grid;
  row-gap: 64px;
  column-gap: 20px;
  grid-template-columns: 1fr 1fr 1fr 1fr;
}
.articles-page__pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 64px;
}

@media (max-width: 1599px) {
  .articles-page__grid {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
@media (max-width: 1199px) {
  .articles-page__grid {
    row-gap: 40px;
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 1023px) {
  .articles-page__grid {
    row-gap: 20px;
  }
  .articles-page__pagination {
    margin-top: 32px;
  }
}
@media (max-width: 859px) {
  .articles-page__grid {
    grid-template-columns: 1fr;
  }
}
</style>
